import { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { Col, Form, Row } from "reactstrap";

import { DynamicFieldRenderer } from "../../../../CommonElements/DynamicFieldRenderer";
import FormFooter from "../../../../CommonElements/FormFooter";
import IconComponent from "../../../../CommonElements/IconComponent";
import { closeModal } from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { AppDispatch } from "../../../../ReduxToolkit/Store";
import {
  useCreateMenuMutation,
  useUpdateMenuMutation,
} from "../../../../ReduxToolkit/api/menus";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import { menuFormFields } from "../../fields";
import { IMenuFormData, IMenusList } from "../../types";
import { menuSchema } from "../../validationSchema/menuSchema";
import { Nullable } from "../../../../Types/Common";

interface IMenuFormProps {
  dispatch: AppDispatch;
  menus: IMenusList[];
  selectedMenu: Nullable<IMenusList>;
}

interface IMenuFormValues {
  name: string;
  route: string;
  icon: string;
  parent_id: string;
  sort_order: string;
  is_default: string;
  status: string;
}

type MenuChild = IMenusList | { data: IMenusList[] };

interface IMenuOptionRow {
  menu: IMenusList;
  level: number;
}

const getChildren = (menu: IMenusList): IMenusList[] => {
  const children = menu.children as MenuChild[] | undefined;

  if (!Array.isArray(children)) return [];

  return children.flatMap((child) => {
    if ("data" in child && Array.isArray(child.data)) {
      return child.data;
    }

    return child as IMenusList;
  });
};

const flattenMenus = (menus: IMenusList[], level = 0): IMenuOptionRow[] =>
  menus.flatMap((menu) => [
    { menu, level },
    ...flattenMenus(getChildren(menu), level + 1),
  ]);

const getMenuIds = (menu: IMenusList): number[] => [
  menu.id,
  ...getChildren(menu).flatMap(getMenuIds),
];

const MenuForm = ({ dispatch, menus, selectedMenu }: IMenuFormProps) => {
  const [createMenu, { isLoading: isCreating, reset: resetCreateMenu }] =
    useCreateMenuMutation();
  const [updateMenu, { isLoading: isUpdating, reset: resetUpdateMenu }] =
    useUpdateMenuMutation();

  const isEditMode = Boolean(selectedMenu?.id);

  const defaultValues: IMenuFormValues = useMemo(
    () => ({
      name: selectedMenu?.name ?? "",
      route: selectedMenu?.route ?? "",
      icon: selectedMenu?.icon ?? "",
      parent_id: String(selectedMenu?.parent_id ?? 0),
      sort_order: String(selectedMenu?.sort_order ?? 0),
      is_default: String(selectedMenu?.is_default ?? 0),
      status: String(selectedMenu?.status ?? 1),
    }),
    [selectedMenu]
  );

  const methods = useForm<IMenuFormValues>({
    resolver: yupResolver(menuSchema) as any,
    defaultValues,
    values: defaultValues,
  });

  const { handleSubmit, reset } = methods;

  const dynamicOptionsMap: Record<string, { label: string; value: string }[]> =
    useMemo(() => {
      const excludedMenuIds = selectedMenu
        ? new Set(getMenuIds(selectedMenu))
        : null;

      const menuOptions = [
        { label: "Select Parent", value: "0" },
        ...flattenMenus(menus)
          .filter(({ menu }) => !excludedMenuIds?.has(menu.id))
          .map(({ menu, level }) => ({
            label: `${"-- ".repeat(level)}${menu.name}`,
            value: menu.id.toString(),
          })),
      ];

      return { menuOptions };
    }, [menus, selectedMenu]);

  const handleReset = () => {
    reset(defaultValues);
    resetCreateMenu();
    resetUpdateMenu();
    dispatch(closeModal());
  };

  const onSubmit = async (values: IMenuFormValues) => {
    const payload: IMenuFormData = {
      name: values.name.trim(),
      route: values.route.trim(),
      icon: values.icon.trim(),
      parent_id: Number(values.parent_id) || null,
      sort_order: Number(values.sort_order),
      is_default: Boolean(Number(values.is_default)),
      status: Number(values.status),
    };

    const response =
      isEditMode && selectedMenu
        ? await updateMenu({ id: selectedMenu.id, data: payload })
        : await createMenu(payload);

    showApiResultAlert(
      response,
      {
        title: isEditMode
          ? "Menu Updated Successfully"
          : "Menu Created Successfully",
        source: "menuFormModal",
      },
      handleReset
    );
  };

  return (
    <FormProvider {...methods}>
      <Form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
        <Row>
          {menuFormFields.map((field) => {
            const options =
              field.dynamicOptions && dynamicOptionsMap[field.dynamicOptions]
                ? dynamicOptionsMap[field.dynamicOptions]
                : field.options ?? [];

            return (
              <Col md={field.col ?? 6} key={field.name}>
                {DynamicFieldRenderer(field, options)}
              </Col>
            );
          })}
        </Row>
        <FormFooter
          onClose={() => dispatch(closeModal())}
          isShowCloseBtn={false}
          saveBtnLg="5"
          saveClassName="w-100"
          isSaveBtnDisabled={isCreating || isUpdating}
          saveLabel={
            isCreating || isUpdating ? (
              <>
                <IconComponent className="fa fa-spinner fa-spin" /> Loading...
              </>
            ) : isEditMode ? (
              "Update"
            ) : (
              "Save"
            )
          }
        />
      </Form>
    </FormProvider>
  );
};

export default MenuForm;
